import { css } from '@emotion/react'
import { ConsultaSeguimentoIndicatorBlock } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorBlock'
import { ConsultaSeguimentoIndicatorHelper } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoPerguntaHeaderTitle'
import {
  ExemplosVerticalField,
  ExemplosVerticalFieldOptions,
} from '@mchat-rf/consulta-seguimento/components/ExemplosVerticalField'
import { PerguntaSimNaoRadioListField } from '@mchat-rf/consulta-seguimento/components/PerguntaSimNaoRadioListField'
import { isPerguntaStatusRespondendo } from '@mchat-rf/consulta-seguimento/consulta-seguimento-aplicacao/utils-consultaSeguimentoAplicacao'
import { PassouFalhouEnum } from '@mchat-rf/consulta-seguimento/enums-consultaSeguimento'
import { useConsultaSeguimentoAutoScroll } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoAutoScroll'
import { useConsultaSeguimentoSetResultado } from '@mchat-rf/consulta-seguimento/hooks/useConsultaSeguimentoSetResultado'
import { Heading, Text, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { RefObject } from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import {
  ConsultaSeguimentoPergunta12ExemplosEnum,
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum,
  ConsultaSeguimentoPergunta12Model,
} from './model-consultaSeguimentoPergunta12'
import { PERGUNTA_12_SCROLL_CONFIG } from './scrollConfig-consultaSeguimentoPergunta12'
import {
  calculateResultadoPergunta12,
  conditionToCalculatePergunta12,
  getPergunta12ResultadoText,
  hasPergunta12ExemplosAtLeastTwoSim,
  hasPergunta12ExemplosFalhouAnySim,
  hasPergunta12ExemplosPassouAnySim,
  isPergunta12ExemplosPassouFalhouAllFilled,
  isPergunta12ExemplosPassouFalhouAllNao,
} from './utils-consultaSeguimentoPergunta12'

interface ConsultaSeguimentoPergunta12FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta12Model>
  containerRef: RefObject<HTMLDivElement>
  goNextButtonRef: RefObject<HTMLButtonElement>
}

export function ConsultaSeguimentoPergunta12Field(props: Readonly<ConsultaSeguimentoPergunta12FieldProps>) {
  const { name, containerRef, goNextButtonRef } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta12Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta12Model>({
    name,
    conditionToCalculate: conditionToCalculatePergunta12,
    calculateResultado: calculateResultadoPergunta12,
  })

  const { getId, triggerScrollFor } = useConsultaSeguimentoAutoScroll<ConsultaSeguimentoPergunta12Model>({
    name,
    containerRef,
    goNextButtonRef,
    scrollConfig: PERGUNTA_12_SCROLL_CONFIG,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_12_HEADER === SimNaoEnum.SIM
  const hasPerguntaExemplosPassouAnySim = hasPergunta12ExemplosPassouAnySim(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const isExemplosAllFilledPassouFalhou = isPergunta12ExemplosPassouFalhouAllFilled(
    value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU
  )
  const isPerguntaExemplosPassouFalhouAllNao = isPergunta12ExemplosPassouFalhouAllNao(
    value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU
  )

  const resultadoText = getPergunta12ResultadoText(value)

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        {isPerguntaStatusRespondendo(value.status) && (
          <ConsultaSeguimentoPerguntaHeader
            pergunta={MChatPerguntaEnum.PERGUNTA_12}
            title={<ConsultaSeguimentoPerguntaHeaderTitle sufix=' fica muito incomodado com barulhos do dia a dia?' />}
            status={value.status}
            field={
              <SimNaoSwitchButtonField
                name={name.PERGUNTA_12_HEADER}
                onChange={() => triggerScrollFor('PERGUNTA_12_HEADER')}
              />
            }
          />
        )}
        {isPerguntaHeaderSim && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
            </VFlow>
            <VFlow vSpacing={0}>
              <div id={getId('PERGUNTA_12_EXEMPLOS')}>
                <PerguntaSimNaoRadioListField<ConsultaSeguimentoPergunta12ExemplosEnum>
                  name={name.PERGUNTA_12_EXEMPLOS}
                  nameOutro={name.OUTRO}
                  label='A criança tem uma reação negativa ao som de...'
                  description='Responda os exemplos até pontuar na Pergunta 12 ou avançar para o próximo fluxo'
                  options={PERGUNTA_12_EXEMPLOS_OPTIONS}
                  onChange={() => triggerScrollFor('PERGUNTA_12_EXEMPLOS')}
                  required
                />
              </div>
              {hasPergunta12ExemplosAtLeastTwoSim(value) && (
                <VFlow id={getId('PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU')}>
                  <VFlow vSpacing={0}>
                    <ConsultaSeguimentoIndicatorStep
                      text='Sim para dois ou mais:'
                      style={styles.exemplosIndicatorStep}
                    />
                    <ConsultaSeguimentoIndicatorHelper
                      title='Como ele reage a esses barulhos?'
                      subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                    />
                  </VFlow>
                  <VFlow vSpacing={0}>
                    <ExemplosVerticalField<ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum>
                      name={name.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU}
                      label='Responda todos os exemplos, conforme as respostas do genitor'
                      options={PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU_OPTIONS}
                      title='A criança...'
                      onChange={() => triggerScrollFor('PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU')}
                      required
                    />
                    {isPerguntaExemplosPassouFalhouAllNao && <ConsultaSeguimentoIndicatorBlock />}
                    {isExemplosAllFilledPassouFalhou &&
                      hasPerguntaExemplosPassouAnySim &&
                      hasPerguntaExemplosFalhouAnySim && (
                        <VFlow vSpacing={0}>
                          <ConsultaSeguimentoIndicatorStep
                            text={
                              <VFlow vSpacing={0}>
                                <Text color='inherit'>Sim tanto para exemplos Passou quanto Falhou:</Text>
                                <Heading level={3} color='inherit'>
                                  Pergunta sobre a frequência
                                </Heading>
                              </VFlow>
                            }
                            style={styles.indicatorStep}
                          />
                          <div id={getId('PERGUNTA_12_FREQUENCIA')}>
                            <RadioGroupBoxField
                              name={name.PERGUNTA_12_FREQUENCIA}
                              label='Quais os que ele faz com mais frequência?'
                              options={PERGUNTA_12_FREQUENCIA_OPTIONS}
                              required
                              onChange={() => triggerScrollFor('PERGUNTA_12_FREQUENCIA')}
                            />
                          </div>
                        </VFlow>
                      )}
                  </VFlow>
                </VFlow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && <ConsultaSeguimentoIndicatorResultado text={resultadoText} resultado={value.resultado} />}
    </VFlow>
  )
}

const PERGUNTA_12_EXEMPLOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta12ExemplosEnum>[] = [
  {
    label: 'Máquina de lavar?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.MAQUINA_DE_LAVAR,
  },
  {
    label: 'Choro de bebês?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.CHORO_DE_BEBES,
  },
  {
    label: 'Aspirador de pó?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.ASPIRADOR_DE_PO,
  },
  {
    label: 'Secador de cabelo?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.SECADOR_DE_CABELO,
  },
  {
    label: 'Trânsito?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.TRANSITO,
  },
  {
    label: 'Grito de bebês?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.GRITO_DE_BEBES,
  },
  {
    label: 'Música alta?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.MUSICA_ALTA,
  },
  {
    label: 'Toque do telefone/campainha?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.TOQUE_TELEFONE_CAMPAINHA,
  },
  {
    label: 'Barulho de liquidificador?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.BARULHO_DE_LIQUIDIFICADOR,
  },
  {
    label: 'Lugares barulhentos como supermercado ou restaurante?',
    value: ConsultaSeguimentoPergunta12ExemplosEnum.LUGARES_BARULHENTOS,
  },
]

const PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU_OPTIONS: ExemplosVerticalFieldOptions<ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum> = {
  PASSOU: [
    {
      label: 'Tampa os ouvidos calmamente?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.TAMPA_OUVIDOS_CALMAMENTE,
    },
    {
      label: 'Diz a você que não gosta de barulho?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.NAO_GOSTA_BARULHO,
    },
  ],
  FALHOU: [
    {
      label: 'Grita?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.GRITA,
    },
    {
      label: 'Chora?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.CHORA,
    },
    {
      label: 'Tampa os ouvidos?',
      value: ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.TAMPA_OUVIDOS,
    },
  ],
}

const PERGUNTA_12_FREQUENCIA_OPTIONS: RadioOption<PassouFalhouEnum>[] = [
  {
    label: 'Exemplos PASSOU',
    value: PassouFalhouEnum.PASSOU,
  },
  {
    label: 'Exemplos FALHOU',
    value: PassouFalhouEnum.FALHOU,
  },
]

const styles = {
  indicatorStep: css`
    width: auto;
  `,
  exemplosIndicatorStep: css`
    width: auto;
  `,
}
